import type { ChatMessage } from '../types/chat';
import { getAssistantMessageKind } from '../chat/assistantMessage';

export interface SessionReadCursor {
  timestamp: string;
  role: ChatMessage['role'];
  content: string;
}

export interface SessionReadoutPlan {
  unreadMessages: ChatMessage[];
  unreadSpeech: string[];
  nextCursor: SessionReadCursor | null;
}

export type SessionReadCursorMatchKind = 'exact' | 'timestamp-role' | 'missing';

export interface SessionReadCursorMatch {
  kind: SessionReadCursorMatchKind;
  index: number;
}

function normalizeMessageContent(content: string): string {
  return content.replace(/\s+/g, ' ').trim();
}

function isTrackableAssistantMessage(message: ChatMessage): boolean {
  return getAssistantMessageKind(message.content) === 'message';
}

export function isSessionReadoutMessage(message: ChatMessage): boolean {
  if (message.isDraft || message.isThinking || message.isStreaming) {
    return false;
  }

  const content = normalizeMessageContent(message.content);
  if (!content) return false;

  if (message.role === 'user') {
    return true;
  }

  if (message.role === 'assistant') {
    return isTrackableAssistantMessage(message);
  }

  return false;
}

export function buildSessionReadCursorFromMessage(
  message: ChatMessage | null | undefined,
): SessionReadCursor | null {
  if (!message || !isSessionReadoutMessage(message)) {
    return null;
  }

  return {
    timestamp: message.timestamp,
    role: message.role,
    content: normalizeMessageContent(message.content),
  };
}

export function buildSessionReadCursor(
  messages: readonly ChatMessage[],
): SessionReadCursor | null {
  for (let index = messages.length - 1; index >= 0; index -= 1) {
    const cursor = buildSessionReadCursorFromMessage(messages[index]);
    if (cursor) {
      return cursor;
    }
  }

  return null;
}

export function findSessionReadCursorMatch(
  messages: readonly ChatMessage[],
  cursor: SessionReadCursor | null | undefined,
): SessionReadCursorMatch {
  if (!cursor) {
    return {
      kind: 'missing',
      index: -1,
    };
  }

  let timestampRoleMatch = -1;

  for (let index = messages.length - 1; index >= 0; index -= 1) {
    const message = messages[index];
    if (!message) continue;
    if (!isSessionReadoutMessage(message)) continue;
    if (message.role !== cursor.role || message.timestamp !== cursor.timestamp) {
      continue;
    }

    timestampRoleMatch = index;
    if (normalizeMessageContent(message.content) === cursor.content) {
      return {
        kind: 'exact',
        index,
      };
    }
  }

  if (timestampRoleMatch >= 0) {
    return {
      kind: 'timestamp-role',
      index: timestampRoleMatch,
    };
  }

  return {
    kind: 'missing',
    index: -1,
  };
}

export function hasSessionReadCursorMatch(
  messages: readonly ChatMessage[],
  cursor: SessionReadCursor | null | undefined,
): boolean {
  if (!cursor) {
    return true;
  }

  return findSessionReadCursorMatch(messages, cursor).kind !== 'missing';
}

export function advanceSessionReadCursor(
  current: SessionReadCursor | null | undefined,
  handledMessage: ChatMessage | null | undefined,
): SessionReadCursor | null {
  return buildSessionReadCursorFromMessage(handledMessage) ?? current ?? null;
}

export function formatSessionReadoutMessage(message: ChatMessage): string | null {
  if (!isSessionReadoutMessage(message)) {
    return null;
  }

  const content = normalizeMessageContent(message.content);
  if (!content) return null;

  if (message.role === 'user') {
    return `You said: ${content}`;
  }

  return content;
}

export function buildSessionReadoutPlan(
  messages: readonly ChatMessage[],
  cursor: SessionReadCursor | null | undefined,
): SessionReadoutPlan {
  const trackableMessages = messages.filter(isSessionReadoutMessage);
  const nextCursor = buildSessionReadCursor(trackableMessages);

  if (trackableMessages.length === 0) {
    return {
      unreadMessages: [],
      unreadSpeech: [],
      nextCursor,
    };
  }

  let startIndex = 0;

  if (cursor) {
    const matchedCursor = findSessionReadCursorMatch(trackableMessages, cursor);

    if (matchedCursor.kind !== 'missing') {
      startIndex = matchedCursor.index + 1;
    } else {
      const firstNewerIndex = trackableMessages.findIndex(
        (message) => message.timestamp > cursor.timestamp,
      );
      startIndex = firstNewerIndex >= 0 ? firstNewerIndex : trackableMessages.length;
    }
  }

  const unreadMessages = trackableMessages.slice(startIndex);
  const unreadSpeech = unreadMessages.flatMap((message) => {
    const spoken = formatSessionReadoutMessage(message);
    return spoken ? [spoken] : [];
  });

  return {
    unreadMessages,
    unreadSpeech,
    nextCursor,
  };
}
